import { AlertCircle, Lightbulb, Loader2 } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

/**
 * Calm empty state when no idea is selected or no results yet.
 */
interface EmptyStateProps {
  title?: string;
  description?: string;
  icon?: React.ComponentType<{ className?: string }>;
  className?: string;
}

export function InstrumentEmptyState({
  title = 'Nothing here yet',
  description = 'Select an idea or run an analysis to see results.',
  icon: Icon = Lightbulb,
  className,
}: EmptyStateProps) {
  return (
    <div className={cn('rounded-lg border border-border bg-muted/20 p-8', className)}>
      <div className="flex flex-col items-center text-center space-y-3 max-w-sm mx-auto">
        <div className="h-10 w-10 rounded-full bg-muted/50 flex items-center justify-center">
          <Icon className="h-5 w-5 text-muted-foreground" />
        </div>
        <div className="space-y-1">
          <p className="text-sm font-medium text-foreground">{title}</p>
          <p className="text-xs text-muted-foreground leading-relaxed">{description}</p>
        </div>
      </div>
    </div>
  );
}

/**
 * Loading skeleton for result cards.
 */
interface LoadingStateProps {
  cardCount?: number;
  className?: string;
}

export function InstrumentLoadingState({ cardCount = 3, className }: LoadingStateProps) {
  return (
    <div className={cn('space-y-4', className)}>
      {/* Summary skeleton */}
      <div className="rounded-lg border border-border bg-muted/20 p-4 space-y-3">
        <Skeleton className="h-4 w-32" />
        <div className="space-y-2">
          <Skeleton className="h-3 w-full" />
          <Skeleton className="h-3 w-4/5" />
          <Skeleton className="h-3 w-3/5" />
        </div>
      </div>

      {/* Card skeletons */}
      <div className="space-y-3">
        {Array.from({ length: cardCount }).map((_, i) => (
          <div key={i} className="rounded-lg border border-border bg-card p-4 space-y-3">
            <div className="flex items-center justify-between">
              <Skeleton className="h-4 w-40" />
              <Skeleton className="h-5 w-16 rounded-full" />
            </div>
            <div className="space-y-2">
              <Skeleton className="h-3 w-full" />
              <Skeleton className="h-3 w-5/6" />
            </div>
            <div className="pt-1">
              <Skeleton className="h-3 w-24" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Non-scary error state with retry option.
 */
interface ErrorStateProps {
  message?: string;
  onRetry?: () => void;
  className?: string;
}

export function InstrumentErrorState({
  message = "Something didn't work as expected.",
  onRetry,
  className,
}: ErrorStateProps) {
  return (
    <div className={cn('rounded-lg border border-border bg-muted/20 p-6', className)}>
      <div className="flex flex-col items-center text-center space-y-3 max-w-sm mx-auto">
        <div className="h-10 w-10 rounded-full bg-amber-500/10 flex items-center justify-center">
          <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
        </div>
        <div className="space-y-1">
          <p className="text-sm font-medium text-foreground">Couldn&apos;t complete the analysis</p>
          <p className="text-xs text-muted-foreground leading-relaxed">{message}</p>
        </div>
        {onRetry && (
          <Button variant="outline" size="sm" onClick={onRetry} className="mt-2">
            Try again
          </Button>
        )}
      </div>
    </div>
  );
}

/**
 * Inline loading indicator for buttons/actions.
 */
interface InlineLoadingProps {
  text?: string;
  className?: string;
}

export function InlineLoading({ text = 'Working...', className }: InlineLoadingProps) {
  return (
    <span className={cn('inline-flex items-center gap-2 text-sm text-muted-foreground', className)}>
      <Loader2 className="h-3.5 w-3.5 animate-spin" />
      {text}
    </span>
  );
}

/**
 * Empty state specifically for when no idea is selected.
 */
export function NoIdeaSelectedState({ className }: { className?: string }) {
  return (
    <InstrumentEmptyState
      title="Choose an idea to begin"
      description="Select one of your ideas above, or proceed without one for a broader search."
      icon={Lightbulb}
      className={className}
    />
  );
}

/**
 * Empty state for results area before any analysis.
 */
export function NoResultsYetState({ 
  instrumentType = 'analysis',
  className 
}: { 
  instrumentType?: 'analysis' | 'search';
  className?: string;
}) {
  const copy = instrumentType === 'search' 
    ? {
        title: 'Ready when you are',
        description: 'Fill in your search criteria above and press Find Products to discover what already exists.',
      }
    : {
        title: 'Ready when you are',
        description: 'Fill in the details above and press Analyze to see what the landscape looks like.',
      };

  return (
    <InstrumentEmptyState
      title={copy.title}
      description={copy.description}
      icon={Lightbulb}
      className={className}
    />
  );
}
